import { useState } from 'react'

import { Comment01Icon, MusicNote01Icon, Share01Icon } from '@hugeicons/react'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import { toast } from 'sonner'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { showErrorAlert } from '@/shared/lib/errors'
import { Card } from '@/shared/ui/Card'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { FeaturedListCardStrip, featuredListCardClassName } from '@/shared/ui/FeaturedListCardStrip'

import { ParticipantPostHeader, ProducerPostHeader } from './PostCard'
import { ReactionBar } from './ReactionBar'
import { useReportPost, useSharePost, useToggleReaction } from '../model/mutations'

// Rodapé: mesmo alvo de toque de 44px que PostCard usa (ACCESSIBILITY.md).
const ACTION_CLASS =
  'flex min-h-11 items-center gap-1.5 rounded-lg px-2 text-sm text-gray-700 transition-colors hover:text-primary'

/**
 * Card compacto de altura fixa (~280px) para o carrossel do modo hub — não é
 * `PostCard` com CSS por cima: `PostCard` tem altura variável (mídia até
 * 320px, comentários expansíveis inline), inviável dentro de um slide de
 * Swiper. Reaproveita cabeçalho, reações e share de `PostCard`/`ReactionBar`
 * em vez de reimplementar; comentários e conteúdo longo delegam para fora do
 * card (`onOpenComments` abre bottom sheet, `onFocusPost` navega para
 * `/feed?post={id}`) em vez de expandir inline.
 */
export const PostCardPreview = ({ post, allowedEmojis = [], onOpenComments, onFocusPost }) => {
  const { eventSlug, digitalProduct } = useDigitalProductContext()
  const [confirmingReport, setConfirmingReport] = useState(false)
  const [reporting, setReporting] = useState(false)
  const { toggleReaction } = useToggleReaction(eventSlug, post.id)
  const { sharePost } = useSharePost(eventSlug, post.id)
  const { reportPost } = useReportPost(eventSlug, post.id)

  const isProducerPost = post.author_type === 'producer'
  const eventName = digitalProduct?.name || 'Evento'
  const eventLogoUrl = digitalProduct?.logo?.file?.url

  const sanitizedContent = DOMPurify.sanitize(post.content)

  const handleReport = async () => {
    setReporting(true)
    try {
      await reportPost()
      setConfirmingReport(false)
      toast.success('Denúncia enviada para a curadoria do evento.')
    } catch (err) {
      showErrorAlert(err)
    } finally {
      setReporting(false)
    }
  }

  // Mesma derivação de PostCard.jsx — post_type só tem 'text'/'video'/'audio';
  // 'text' com media_url é imagem.
  const hasMedia = !!post.media_url
  const isVideo = post.post_type === 'video'
  const isAudio = post.post_type === 'audio'
  const isImage = hasMedia && !isVideo && !isAudio

  return (
    <Card
      flush
      shadow={post.is_featured ? undefined : 'sm'}
      data-testid="feed-post-preview"
      className={clsx(
        'card-gamification flex h-[280px] flex-col overflow-hidden',
        post.is_featured && featuredListCardClassName('mission'),
        post.is_sponsored && 'border-l-4 border-l-blue-400'
      )}
    >
      {post.is_featured && (
        <FeaturedListCardStrip label="Postagem em destaque" tone="mission" compact />
      )}
      {isProducerPost ? (
        <ProducerPostHeader
          eventName={eventName}
          eventLogoUrl={eventLogoUrl}
          publishedAt={post.published_at}
        />
      ) : (
        <ParticipantPostHeader post={post} onReport={() => setConfirmingReport(true)} />
      )}

      <button
        type="button"
        onClick={() => onFocusPost(post.id)}
        className="flex min-h-0 flex-1 items-start gap-3 px-4 pt-2.5 text-left"
      >
        <div
          className="prose prose-sm line-clamp-3 min-w-0 flex-1 whitespace-pre-line text-base text-gray-900"
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
        {(isImage || isVideo) && (
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-100">
            {isVideo ? (
              <video src={post.media_url} className="h-full w-full object-cover" muted preload="metadata" />
            ) : (
              <img src={post.media_url} alt="" className="h-full w-full object-cover" />
            )}
          </div>
        )}
        {isAudio && (
          <div
            aria-hidden
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-gray-100"
          >
            <MusicNote01Icon size={20} variant="stroke" className="text-gray-500" />
          </div>
        )}
      </button>

      <div className="shrink-0 px-4 pb-3 pt-2">
        <ReactionBar
          allowedEmojis={allowedEmojis}
          reactionsSummary={post.reactions_summary}
          myReaction={post.my_reaction}
          onToggle={toggleReaction}
        />
        <div className="mt-1 flex items-center justify-between border-t border-border pt-1">
          <button
            type="button"
            onClick={() => onOpenComments(post.id)}
            className={ACTION_CLASS}
          >
            <Comment01Icon size={16} variant="stroke" />
            {post.comments_count} comentário{post.comments_count !== 1 ? 's' : ''}
          </button>
          <button type="button" onClick={sharePost} className={ACTION_CLASS}>
            <Share01Icon size={16} variant="stroke" />
            Compartilhar
          </button>
        </div>
      </div>

      {confirmingReport && !isProducerPost && (
        <ConfirmDialog
          variant="destructive"
          title="Denunciar publicação"
          description="A publicação vai para a curadoria do evento, que decide se ela sai do feed. Você pode denunciar só uma vez."
          confirmLabel="Denunciar"
          confirmLoading={reporting}
          onConfirm={handleReport}
          onCancel={() => setConfirmingReport(false)}
        />
      )}
    </Card>
  )
}
